import { format } from "date-fns";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useLanguage } from "hooks/useSelectedLanguage";

import type { TableRow } from "components/SortableTable/SortableTable.model";
import { InviteBundleStatusTag } from "../InviteBundleStatusTag/InviteBundleStatusTag";

import { StyledTableContainer } from "pages/AdminEnvironment/AdminPage/subpages/UsersPage/components/StudentsTable/StudentsTable.styled";

import { FontAwesomeIcon } from "components/FontAwesomeIcon";

import { TeacherModals } from "constants/modals-constants";
import { StyledBundleTable, StyledButton } from "./InvitationBundleList.styled";

const TYPE_FA_ICON_MAP: Record<string, string> = {
  email: "fas fa-envelope",
  link: "far fa-link",
};

export const InvitationBundleList: React.FC = observer(() => {
  const { t } = useTranslation("teacher-dashboard");
  const { openModal } = useModal();
  const { currentLanguage } = useLanguage();
  const { loading, invitationBundleList, fetchInvitationBundleList } = useProduct();

  useEffect(() => {
    fetchInvitationBundleList();
  }, [fetchInvitationBundleList]);

  const tableData = useMemo<TableRow[]>(
    () =>
      invitationBundleList.inviteBundles.map((bundle, index) => {
        const {
          type,
          products,
          groupName,
          licenseExpirationDate,
          totalInvites,
          totalAcceptedInvites,
        } = bundle;

        return {
          id: `bundle-${index}-row`,
          dataCy: "bundle-row",
          cells: [
            {
              id: `bundle-${index}-type`,
              dataCy: "bundle-type",
              content: (
                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <FontAwesomeIcon iconName={TYPE_FA_ICON_MAP[type]} />
                  {t(`studentLicense.invitationBundleList.types.${type}`)}
                </span>
              ),
            },
            {
              id: `bundle-${index}-product`,
              dataCy: "bundle-product",
              content: products.join(", "),
            },
            {
              id: `bundle-${index}-group`,
              dataCy: "bundle-group",
              content: groupName ?? "-",
            },
            {
              id: `bundle-${index}-expiry-date`,
              dataCy: "bundle-expiry-date",
              content: format(new Date(licenseExpirationDate), "dd-MM-yyyy"),
            },
            {
              id: `bundle-${index}-usage`,
              dataCy: "bundle-usage",
              content: `${totalAcceptedInvites} / ${totalInvites}`,
            },
            {
              id: `bundle-${index}-status`,
              dataCy: "bundle-status",
              content: (
                <InviteBundleStatusTag
                  currentUsage={totalAcceptedInvites}
                  maxUsage={totalInvites}
                  expirationDate={licenseExpirationDate}
                />
              ),
            },
            {
              id: `bundle-${index}-action`,
              dataCy: "bundle-action",
              content: (
                <StyledButton
                  dataCy="bundle-view-button"
                  disabled={loading}
                  variant="text"
                  onClick={() => {
                    // TODO: create enum with bundle types
                    // TODO: rellocate this handler to a named method
                    if (bundle.type === "email") {
                      openModal(TeacherModals.ViewEmailInviteBundleDetails, {
                        emailInviteBundleId: bundle.id,
                      });
                    }
                  }}
                >
                  <FontAwesomeIcon iconName="fas fa-eye" style={{ marginRight: "0.4rem" }} />
                  {t("studentLicense.invitationBundleList.actions.view")}
                </StyledButton>
              ),
            },
          ],
        };
      }),
    [invitationBundleList.inviteBundles, loading, t, currentLanguage],
  );

  return (
    <StyledTableContainer>
      <StyledBundleTable
        flexStartCellHeaders
        headerCells={[
          {
            id: "bundle-type-column",
            title: t("studentLicense.invitationBundleList.columns.type"),
            size: "1fr",
          },
          {
            id: "bundle-product-column",
            title: t("studentLicense.invitationBundleList.columns.product"),
            size: "1fr",
          },
          {
            id: "bundle-group-column",
            title: t("studentLicense.invitationBundleList.columns.group"),
            size: "1fr",
          },
          {
            id: "bundle-expiry-date-column",
            title: t("studentLicense.invitationBundleList.columns.expiryDate"),
            size: "1fr",
          },
          {
            id: "bundle-usage-column",
            title: t("studentLicense.invitationBundleList.columns.usage"),
            size: "1fr",
          },
          {
            id: "bundle-status-column",
            title: t("studentLicense.invitationBundleList.columns.status"),
            size: "1fr",
          },
          {
            id: "bundle-action-column",
            title: t("studentLicense.invitationBundleList.columns.action"),
            size: "auto",
          },
        ]}
        rows={tableData}
      />
    </StyledTableContainer>
  );
}) as React.FC;
